'use client';

import { useRouter } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { FormField } from '@/components/ui/form-field';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useRegistration } from '@/hooks/use-registration';
import { useFormValidation } from '@/hooks/use-form-validation';
import { Loader2, Brain, AlertCircle } from 'lucide-react';
import Link from 'next/link';

const initialValues = {
  name: '',
  email: '',
  password: '',
};

const validationRules = {
  name: { 
    required: true, 
    minLength: 3,
    pattern: /^[A-Za-zÀ-ÖØ-öø-ÿ\s]+$/
  },
  email: { 
    required: true, 
    pattern: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    isEmail: true
  },
  password: { 
    required: true, 
    minLength: 6
  }
};

export default function RegisterPage() {
  const router = useRouter();
  const { register, loading, error, setError } = useRegistration();
  const {
    values,
    errors,
    touched,
    handleChange,
    isValid
  } = useFormValidation(initialValues, validationRules);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!isValid()) {
      const errorMessages = Object.entries(errors)
        .filter(([field, error]) => touched[field] && error)
        .map(([field, error]) => error)
        .join('\n');
      
      setError(errorMessages || "Por favor, verifique os campos do formulário");
      return;
    }

    console.log('Iniciando registro na página:', values);

    const success = await register(values);
    console.log('Resultado do registro na página:', success);

    if (success) {
      router.push('/dashboard');
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-background to-muted p-4">
      <Card className="w-full max-w-md p-8">
        <div className="flex flex-col items-center mb-8">
          <Brain className="h-12 w-12 text-primary mb-4" />
          <h1 className="text-2xl font-bold">Criar Conta</h1>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <FormField
            label="Nome completo"
            name="name"
            value={values.name}
            onChange={handleChange}
            error={touched.name ? errors.name : ''}
            required
            placeholder="João Silva"
          />
          
          <FormField
            label="Email"
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            error={touched.email ? errors.email : ''}
            required
            placeholder="joao@exemplo.com"
          />
          
          <FormField
            label="Senha"
            name="password"
            type="password"
            value={values.password}
            onChange={handleChange}
            error={touched.password ? errors.password : ''}
            required
            placeholder="Mínimo 6 caracteres"
          />

          <Button 
            type="submit" 
            className="w-full" 
            disabled={loading || !isValid()}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Criando conta...
              </>
            ) : (
              'Criar conta'
            )}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Já tem uma conta?{' '}
            <Link href="/login" className="text-primary hover:underline">
              Entrar
            </Link>
          </p>
        </div>
      </Card>
    </div>
  );
}